import { useState } from 'react';
import {
  createProjectAvatar,
  createProjectAvatarFromPersona,
  instantiateAvatarInSession,
  generateAvatarSystemPrompt
} from '../services/avatarCatalogService';
import { AvatarDefinition } from '../types';

export interface AvatarCreationPersona {
  personaId: string;
  name: string;
  role: string;
  goals?: string[];
  painPoints?: string[];
}

export interface AvatarCreationState {
  name: string;
  setName: (name: string) => void;
  description: string;
  setDescription: (description: string) => void;
  generating: boolean;
  systemPrompt: string;
  keywords: string[];
  saving: boolean;
  error: string;
  showPromptPreview: boolean;
  setShowPromptPreview: (updater: (prev: boolean) => boolean) => void;
  handleGeneratePrompt: () => Promise<void>;
  handleCreateManual: () => Promise<void>;
  handleCreateFromPersona: () => Promise<void>;
}

export function useAvatarCreation(
  mode: 'manual' | 'from-persona',
  projectId: string,
  sessionId: string | undefined,
  persona: AvatarCreationPersona | undefined,
  onCreated: (avatar: AvatarDefinition) => void
): AvatarCreationState {
  const [name, setName] = useState(persona?.name || '');
  const [description, setDescription] = useState(persona?.role || '');
  const [generating, setGenerating] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [keywords, setKeywords] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showPromptPreview, setShowPromptPreview] = useState(false);

  const handleGeneratePrompt = async () => {
    if (!name.trim() || !description.trim()) {
      setError('Name and description are required to generate a prompt.');
      return;
    }
    setError('');
    setGenerating(true);
    try {
      const result = await generateAvatarSystemPrompt(
        projectId,
        name.trim(),
        description.trim(),
        persona?.goals,
        persona?.painPoints
      );
      setSystemPrompt(result.systemPrompt);
      setKeywords(result.keywords);
      setShowPromptPreview(true);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to generate prompt');
    } finally {
      setGenerating(false);
    }
  };

  const handleCreateManual = async () => {
    if (!systemPrompt) {
      setError('Please generate the AI prompt first.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const result = await createProjectAvatar(projectId, {
        name: name.trim(),
        description: description.trim(),
        systemPrompt,
        keywords: keywords.length > 0 ? keywords : [name.toLowerCase()]
      });
      const avatar = result.avatar;

      if (sessionId) {
        try {
          await instantiateAvatarInSession(sessionId, avatar.avatarDefinitionId);
        } catch (instantiateErr: any) {
          // Non-fatal — avatar was created; instantiation can be retried
          console.warn('Could not instantiate avatar in session:', instantiateErr);
        }
      }

      onCreated(avatar);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to create avatar');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateFromPersona = async () => {
    if (!persona) return;
    setSaving(true);
    setError('');
    try {
      if (!sessionId) {
        setError('Session ID is required to create an avatar from a persona.');
        setSaving(false);
        return;
      }
      const result = await createProjectAvatarFromPersona(
        projectId,
        persona.personaId,
        sessionId
      );
      const avatar = result.avatar;

      try {
        await instantiateAvatarInSession(sessionId, avatar.avatarDefinitionId);
      } catch (instantiateErr: any) {
        console.warn('Could not instantiate avatar in session:', instantiateErr);
      }

      onCreated(avatar);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to create avatar from persona');
    } finally {
      setSaving(false);
    }
  };

  return {
    name,
    setName,
    description,
    setDescription,
    generating,
    systemPrompt,
    keywords,
    saving,
    error,
    showPromptPreview,
    setShowPromptPreview,
    handleGeneratePrompt,
    handleCreateManual,
    handleCreateFromPersona
  };
}
